import {useEffect} from 'react';
import {WidgetProps} from '@wix/yoshi-flow-editor';
import type {CheckoutBrand} from '@wix/ambassador-ecom-v1-ecommerce-settings/types';
import type {LoadingStateEcommerceSettings} from '@wix/ecom-platform-viewer-script';
import {
  MessageSource,
  MessageType,
  type DesignChangeMessage,
  type ReadyMessage,
  type SettingsChangeMessage,
} from '../../../domain/composer/messages';
import {CheckoutControllerProps} from '../../../types/checkoutApp.types';

export type AttachComposerListenerParams = {
  onBrandOverride: (brand?: CheckoutBrand) => void;
  onCheckoutSettingsOverride: (checkoutSettings?: LoadingStateEcommerceSettings['checkoutSettings']) => void;
  win?: Window;
};

export function attachComposerListener({
  onBrandOverride,
  onCheckoutSettingsOverride,
  win,
}: AttachComposerListenerParams): () => void {
  const targetWindow = win ?? window;

  const handler = (event: MessageEvent): void => {
    const data = event.data as Partial<DesignChangeMessage> | Partial<SettingsChangeMessage>;
    if (data?.source !== MessageSource.Composer) {
      return;
    }
    if (data.type === MessageType.DesignChange) {
      onBrandOverride(data.payload?.brand);
    }
    if (data.type === MessageType.SettingsChange) {
      onCheckoutSettingsOverride(data.payload?.checkoutSettings);
    }
  };

  targetWindow.addEventListener('message', handler);

  const ready: ReadyMessage = {source: MessageSource.Viewer, type: MessageType.Ready};
  targetWindow.parent?.postMessage(ready, '*');

  return () => targetWindow.removeEventListener('message', handler);
}

export const useComposerListener = (controllerProps: WidgetProps<CheckoutControllerProps>): void => {
  const isBMPreview = controllerProps?.navigationStore?.isBMPreview ?? false;
  const onBrandOverride = controllerProps?.checkoutSettingsStore?.onBrandOverride;
  const onCheckoutSettingsOverride = controllerProps?.checkoutSettingsStore?.onCheckoutSettingsOverride;

  useEffect(() => {
    if (!isBMPreview || !onBrandOverride) {
      return;
    }
    return attachComposerListener({onBrandOverride, onCheckoutSettingsOverride});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBMPreview, !onBrandOverride]);
};
